import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, portal = null) => {
    try {
      const body = { email, password };
      if (portal) body.portal = portal;
      const { data } = await api.post("/auth/login", body);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Portal separation (iteration 29): 403 { error: "wrong_portal", portal_url: "/admin/login" }
      const isWrongPortal = detail && typeof detail === "object" && detail.error === "wrong_portal";
      return {
        ok: false,
        error: formatApiErrorDetail(detail) || e.message,
        wrongPortal: isWrongPortal ? detail.portal_url : null,
      };
    }
  };

  const register = async (body) => {
    try {
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
